import { useCallback, useEffect, useMemo, useState } from "react";
import "../../styles/settings.css";
import SettingsRow from "./SettingsRow";
import SettingsFieldRenderer from "./SettingsFieldRenderer";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { API_BASE } from "../../config";

const SETTINGS_DATASET_TYPE = "SETTINGS_DATASET";

function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isValidSettingsDataset(data) {
    return (
        isPlainObject(data) &&
        data.type === SETTINGS_DATASET_TYPE &&
        isPlainObject(data.fields)
    );
}

function cloneJson(value) {
    return JSON.parse(JSON.stringify(value));
}

function buildSettingsUrl(searchSpace) {
    const url = new URL(`${API_BASE}/ainstein/settings`);

    if (searchSpace) {
        url.searchParams.set("search_space", searchSpace);
    }

    return url.toString();
}

async function readJsonResponse(response) {
    const text = await response.text();

    if (!text) {
        return null;
    }

    try {
        return JSON.parse(text);
    } catch {
        throw new Error("Backend did not return valid JSON.");
    }
}

function SettingsPage({ searchSpace }) {
    const [settingsData, setSettingsData] = useState(null);
    const [initialSettingsData, setInitialSettingsData] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState({
        type: "",
        message: "",
    });

    const loadSettings = useCallback(async () => {
        setIsLoading(true);
        setLoadError("");
        setSaveStatus({ type: "", message: "" });

        try {
            console.log("searchSpace:", searchSpace);
            console.log("settings url:", buildSettingsUrl(searchSpace));

            const response = await fetchWithAuth(buildSettingsUrl(searchSpace), {
                method: "GET",
                headers: {
                    Accept: "application/json",
                },
            });

            let data = null;

            try {
                data = await readJsonResponse(response);
            } catch (error) {
                if (response.ok) {
                    throw error;
                }
            }

            if (!response.ok) {
                throw new Error(
                    data?.message ||
                    `Missing authorization or failed to load settings (status ${response.status}).`
                );
            }

            if (!isValidSettingsDataset(data)) {
                throw new Error("Invalid settings dataset returned by backend.");
            }

            if (searchSpace && data.usecase && data.usecase !== searchSpace) {
                console.warn(
                    `Settings dataset usecase "${data.usecase}" does not match current searchSpace "${searchSpace}".`
                );
            }

            const cloned = cloneJson(data);
            setSettingsData(cloned);
            setInitialSettingsData(cloneJson(cloned));
        } catch (error) {
            setLoadError(
                error?.message ||
                "Missing authorization, contact your administrator."
            );
            setSettingsData(null);
            setInitialSettingsData(null);
        } finally {
            setIsLoading(false);
        }
    }, [searchSpace]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const fieldEntries = useMemo(() => {
        return Object.entries(settingsData?.fields || {});
    }, [settingsData]);

    const isDirty = useMemo(() => {
        if (!settingsData || !initialSettingsData) {
            return false;
        }

        return JSON.stringify(settingsData) !== JSON.stringify(initialSettingsData);
    }, [settingsData, initialSettingsData]);

    const handleFieldChange = useCallback((fieldKey, nextValue) => {
        setSettingsData((prev) => {
            if (!prev?.fields?.[fieldKey]) {
                return prev;
            }

            return {
                ...prev,
                fields: {
                    ...prev.fields,
                    [fieldKey]: {
                        ...prev.fields[fieldKey],
                        value: nextValue,
                    },
                },
            };
        });

        setSaveStatus({ type: "", message: "" });
    }, []);

    const handleSave = useCallback(async () => {
        if (!settingsData || !isValidSettingsDataset(settingsData)) {
            setSaveStatus({
                type: "error",
                message: "Cannot save because the settings dataset is invalid.",
            });
            return;
        }

        setIsSaving(true);
        setSaveStatus({ type: "", message: "" });

        try {
            const datasetToSave = cloneJson(settingsData);

            if (searchSpace) {
                datasetToSave.usecase = searchSpace;
            }

            const response = await fetchWithAuth(buildSettingsUrl(searchSpace), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(datasetToSave),
            });

            let returnedData = null;

            try {
                returnedData = await readJsonResponse(response);
            } catch (jsonError) {
                if (response.ok) {
                    returnedData = null;
                } else {
                    throw jsonError;
                }
            }

            if (!response.ok) {
                throw new Error(
                    returnedData?.message ||
                    `Error while saving settings (status ${response.status}).`
                );
            }

            if (isValidSettingsDataset(returnedData)) {
                const cloned = cloneJson(returnedData);
                setSettingsData(cloned);
                setInitialSettingsData(cloneJson(cloned));
            } else {
                setInitialSettingsData(cloneJson(datasetToSave));
            }

            setSaveStatus({
                type: "success",
                message: "Settings saved successfully.",
            });
        } catch (error) {
            setSaveStatus({
                type: "error",
                message:
                    error?.message || "An unexpected error occurred while saving.",
            });
        } finally {
            setIsSaving(false);
        }
    }, [searchSpace, settingsData]);

    return (
        <div className="settings-shell">
            <div className="settings-main">
                <div className="settings-inner">
                    <div className="settings-header-row">
                        <h2 className="settings-title">Settings</h2>
                    </div>

                    <div className="settings-card">
                        {isLoading && (
                            <div className="settings-feedback settings-feedback--info">
                                Loading settings...
                            </div>
                        )}

                        {!isLoading && loadError && (
                            <div className="settings-feedback settings-feedback--error">
                                {loadError}
                            </div>
                        )}

                        {!isLoading && !loadError && saveStatus.message && (
                            <div
                                className={`settings-feedback ${saveStatus.type === "success"
                                    ? "settings-feedback--success"
                                    : "settings-feedback--error"
                                    }`}
                            >
                                {saveStatus.message}
                            </div>
                        )}

                        {!isLoading && !loadError && settingsData && (
                            <>
                                <div className="settings-list">
                                    {fieldEntries.length === 0 ? (
                                        <div className="settings-placeholder">
                                            No settings fields were returned by the backend.
                                        </div>
                                    ) : (
                                        fieldEntries.map(([fieldKey, field]) => {
                                            const inputId = `settings-field-${fieldKey}`;

                                            return (
                                                <SettingsRow
                                                    key={fieldKey}
                                                    htmlFor={inputId}
                                                    label={field.display_name || fieldKey}
                                                    description={field.description || ""}
                                                    alignTop={Boolean(field.multiline)}
                                                >
                                                    <SettingsFieldRenderer
                                                        id={inputId}
                                                        fieldKey={fieldKey}
                                                        field={field}
                                                        disabled={isSaving}
                                                        onChange={handleFieldChange}
                                                    />
                                                </SettingsRow>
                                            );
                                        })
                                    )}
                                </div>

                                <div className="settings-footer">
                                    <button
                                        type="button"
                                        className="settings-save-button"
                                        onClick={handleSave}
                                        disabled={isSaving || !isDirty}
                                    >
                                        {isSaving ? "Saving..." : "Save"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SettingsPage;